import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Pencil } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import type { TrueupLine } from "@hp/types";
import { Badge, Button, Card, CardBody } from "@hp/ui";
import { fetchTrueupLedger, fetchTrueupSummary, saveTrueupReason } from "../../api/trueup";
import { EmptyState, ErrorState, LoadingState } from "../../components/Feedback";
import { HoverHint } from "../../components/HoverHint";
import { PageHeader } from "../../components/layout/PageHeader";
import { SourceDocumentViewer } from "../../components/SourceDocumentViewer";

type View = "cut" | "revised" | "all";

const VIEWS: Array<{ key: View; label: string; hint: string }> = [
  { key: "cut", label: "Items cut", hint: "Where MPERC allowed less than HP claimed (by Rs 1 Crore or 1% or more), or used a lower rate than HP filed, plus lines a person should check." },
  { key: "revised", label: "Revised later", hint: "Earlier years a later true-up order re-decided (e.g. RoE re-worked with tax grossing-up), with the figure before and after." },
  { key: "all", label: "All lines", hint: "Every line read from HP's true-up orders, including items allowed in full, totals and deductions." },
];

const REASON_TONE: Record<string, "warning" | "info" | "neutral" | "brand" | "danger"> = {
  regulation_cap_or_norm: "neutral",
  prudence_or_discretion: "warning",
  documents_or_reconciliation: "info",
  carried_from_earlier_order: "brand",
  other: "neutral",
};

const REASON_HINT: Record<string, string> = {
  regulation_cap_or_norm: "A regulation fixes it (a norm, a set rate, the applicable tax rate). Harder to challenge; a ruling reading the same rule more favourably can help.",
  prudence_or_discretion: "MPERC judged the cost not prudent, not justified, beyond the original scope or not approved. More arguable; other projects' rulings carry weight.",
  documents_or_reconciliation: "Cut for missing documents or figures that did not reconcile with the audited accounts. Often recoverable by filing the missing proof.",
  carried_from_earlier_order: "Follows an earlier order's decision (e.g. opening capital cost), or is the knock-on of a capital cost cut.",
  other: "The analysis does not state a clear reason, or it fits none of the classes. Read the order.",
};

const REASON_LABEL: Record<string, string> = {
  regulation_cap_or_norm: "Regulation cap or norm",
  prudence_or_discretion: "Prudence or discretion",
  documents_or_reconciliation: "Documents or reconciliation",
  carried_from_earlier_order: "Carried from an earlier order",
  other: "Other",
};

const RATE_NAME: Record<string, string> = {
  base_rate: "Base RoE",
  tax_rate: "Tax rate",
  interest_rate: "Interest rate",
  depreciation_rate: "Depreciation rate",
};

const SLUG = { disallowed_to_allowed: "cil-disallowed-to-allowed", allowed_to_disallowed: "cil-allowed-to-disallowed" } as const;

function crore(value: number | null): string {
  return value === null || value === undefined ? "-" : `Rs ${value.toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr`;
}

function blockLabel(block: string): string {
  if (block === "fgd") return "FGD system";
  if (block.startsWith("unit-")) return `Unit ${block.slice(5)}`;
  return "Project";
}

function rates(line: TrueupLine): string | null {
  const keys = [...new Set([...Object.keys(line.claimed_rates), ...Object.keys(line.allowed_rates)])];
  if (!keys.length) return null;
  return keys
    .map((k) => {
      const filed = line.claimed_rates[k];
      const used = line.allowed_rates[k];
      const name = RATE_NAME[k] ?? k;
      if (filed !== undefined && used !== undefined && filed !== used) return `${name}: ${filed}% filed, ${used}% used`;
      return `${name}: ${used ?? filed}%`;
    })
    .join(" · ");
}

function isCut(line: TrueupLine): boolean {
  return line.kind !== "revision" && (line.material || line.notes.length > 0);
}

/**
 * Why the item was cut. The AI's reading can be edited or confirmed by a
 * reviewer; saving it unchanged confirms it. A reviewer's reason is kept when
 * the order is read again, and corrections are shown to the AI on later
 * readings, so its classification improves with use.
 */
function ReasonCell({ line }: { line: TrueupLine }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = React.useState(false);
  const [reasonClass, setReasonClass] = React.useState(line.reason_class ?? "other");
  const [text, setText] = React.useState(line.reason_text ?? "");
  const mutation = useMutation({
    mutationFn: () => saveTrueupReason(line.id, { reason_class: reasonClass, reason_text: text.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["trueup"] });
      setEditing(false);
    },
  });
  const unchanged = reasonClass === (line.reason_class ?? "other") && text.trim() === (line.reason_text ?? "").trim();
  const canEdit = line.kind !== "revision" && (isCut(line) || Boolean(line.reason_class));
  if (editing) {
    return (
      <div className="flex flex-col gap-2">
        <select className="rounded-md border border-zinc-300 px-2 py-1 text-sm" value={reasonClass} onChange={(e) => setReasonClass(e.target.value)} aria-label="Kind of reason">
          {Object.entries(REASON_LABEL).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        <p className="text-xs text-zinc-500">{REASON_HINT[reasonClass]}</p>
        <textarea
          className="min-h-[72px] rounded-md border border-zinc-300 px-2 py-1 text-sm"
          value={text}
          maxLength={600}
          onChange={(e) => setText(e.target.value)}
          placeholder="Why MPERC allowed less, in one plain sentence"
          aria-label="Why it was cut"
        />
        {mutation.isError ? <span className="text-xs text-rose-700">Could not save. Try again.</span> : null}
        <div className="flex gap-2">
          <Button size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending || text.trim().length < 3}>
            {unchanged && line.reason_class ? "Confirm as is" : "Save"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
        </div>
        <span className="text-xs text-zinc-500">Your version replaces the AI's, stays when the order is read again, and is shown to the AI as a worked example.</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {line.reason_class ? (
        <>
          <div className="flex flex-wrap items-center gap-1">
            <HoverHint hint={REASON_HINT[line.reason_class] ?? line.reason_class}>
              <Badge tone={REASON_TONE[line.reason_class] ?? "neutral"}>{line.reason_label ?? line.reason_class}</Badge>
            </HoverHint>
            {line.reason_source === "person" ? <Badge tone="success">Checked by {line.reason_edited_by}</Badge> : null}
          </div>
          <span className="text-zinc-700">{line.reason_text}</span>
          {line.reason_quote ? (
            <span className="text-xs italic text-zinc-500">
              “{line.reason_quote}”{line.reason_page ? ` p${line.reason_page}` : ""}
            </span>
          ) : null}
          {line.ai_reason_label ? (
            <span className="text-xs text-zinc-500">
              The AI said: {line.ai_reason_label}. {line.ai_reason_text}
            </span>
          ) : null}
        </>
      ) : isCut(line) ? (
        <span className="text-xs text-zinc-500">Reason not read yet</span>
      ) : null}
      {canEdit ? (
        <button
          type="button"
          className="mt-1 inline-flex w-fit items-center gap-1 text-xs font-medium text-[var(--color-accent-content)] hover:underline"
          onClick={() => {
            setReasonClass(line.reason_class ?? "other");
            setText(line.reason_text ?? "");
            setEditing(true);
          }}
        >
          <Pencil className="h-3 w-3" aria-hidden /> {line.reason_class ? "Edit / confirm" : "Add reason"}
        </button>
      ) : null}
    </div>
  );
}

function LineRow({ line, onOpen }: { line: TrueupLine; onOpen: (line: TrueupLine) => void }) {
  const rateText = rates(line);
  return (
    <tr className="border-b border-zinc-100 align-top last:border-0">
      <td className="px-2 py-2 whitespace-nowrap font-medium text-zinc-900">{line.financial_year}</td>
      <td className="px-2 py-2">
        <div className="font-medium text-zinc-900">{line.label}</div>
        <div className="text-xs text-zinc-500">{blockLabel(line.block)}</div>
        {!line.is_item ? <div className="text-xs text-zinc-500">(total or deduction, not a claim item)</div> : null}
      </td>
      <td className="px-2 py-2 whitespace-nowrap tabular-nums">{line.kind === "revision" ? crore(null) : crore(line.claimed_cr)}</td>
      <td className="px-2 py-2 whitespace-nowrap tabular-nums">{crore(line.allowed_cr)}</td>
      <td className="px-2 py-2 whitespace-nowrap tabular-nums font-semibold">
        {line.gap_cr !== null && line.gap_cr > 0 ? <span className="text-rose-700">{crore(line.gap_cr)}</span> : line.kind === "tax_rate" ? <span className="text-rose-700">Rate cut</span> : <span className="text-zinc-500">-</span>}
      </td>
      <td className="px-2 py-2 text-xs text-zinc-700">{rateText ?? "-"}</td>
      <td className="px-2 py-2 text-sm">
        {line.notes.map((note) => (
          <p key={note} className="mb-1 text-xs text-amber-800">{note}</p>
        ))}
        <ReasonCell line={line} />
      </td>
      <td className="px-2 py-2 text-xs text-zinc-700">
        {line.what_next.length ? (
          <ul className="list-disc space-y-1 pl-4">
            {line.what_next.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        ) : (
          "-"
        )}
        {line.change_event_ids.map((id, i) => (
          <Link key={id} to={`/popup/${SLUG[line.change_event_directions[i] ?? "disallowed_to_allowed"]}/${id}`} className="mt-1 block font-medium text-[var(--color-accent-content)] hover:underline">
            Open changed event →
          </Link>
        ))}
      </td>
      <td className="px-2 py-2">
        <div className="text-xs text-zinc-600">{line.order_label}</div>
        {line.open_decision_id ? (
          <Button size="sm" variant="ghost" onClick={() => onOpen(line)}>
            <FileText className="h-4 w-4" aria-hidden /> {line.page ? `p${line.page}` : "Open"}
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * HP's true-up orders as a ledger (GET /trueup/ledger): per year and
 * component, what HP claimed, what MPERC allowed, the gap, the rates each
 * side used, why it was cut (AI reading of MPERC's analysis) and what
 * happened next (revised later, appealed, time limits, knock-on of a
 * capital cost cut).
 */
export function TrueupGapsPage() {
  const [params, setParams] = useSearchParams();
  const view = (VIEWS.find((v) => v.key === params.get("view"))?.key ?? "cut") as View;
  const [year, setYear] = React.useState<string>("all");
  const [opened, setOpened] = React.useState<TrueupLine | null>(null);
  const summary = useQuery({ queryKey: ["trueup", "summary"], queryFn: fetchTrueupSummary });
  const query = useQuery({ queryKey: ["trueup", "ledger"], queryFn: fetchTrueupLedger });
  const rows = query.data ?? [];
  const years = [...new Set(rows.map((r) => r.financial_year))].sort().reverse();
  const keep = (r: TrueupLine) => (view === "cut" ? isCut(r) : view === "revised" ? r.kind === "revision" : true);
  const shown = rows.filter((r) => keep(r) && (year === "all" || r.financial_year === year));
  return (
    <div className="page-stack">
      <PageHeader
        title="True-up gaps"
        description="What HP claimed in each MPERC true-up order, what was allowed, why it was cut, and what happened next. Every gap is a possible claim; how strong it is depends on why it was cut."
      />
      {summary.isSuccess ? (
        <p className="text-sm text-zinc-700">
          {summary.data.orders} true-up orders read · {summary.data.material_gaps} items cut · HP short by{" "}
          <span className="font-semibold text-rose-700">{crore(summary.data.total_gap_cr)}</span> in total (project level, before HP's contracted share)
          {summary.data.reasons_checked ? (
            <>
              {" "}· {summary.data.reasons_checked} reasons checked by people, {summary.data.reasons_changed ?? 0} changed
            </>
          ) : null}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" aria-label="Which lines" className="flex flex-wrap gap-2">
          {VIEWS.map((v) => (
            <HoverHint key={v.key} hint={v.hint} delayMs={1000}>
              <button
                role="tab"
                aria-selected={view === v.key}
                onClick={() => setParams({ view: v.key }, { replace: true })}
                className={["rounded-md px-3 py-1.5 text-sm font-medium", view === v.key ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"].join(" ")}
              >
                {v.label}
                {query.data ? (
                  <span className={["ml-1.5 tabular-nums", view === v.key ? "text-zinc-300" : "text-zinc-500"].join(" ")}>
                    {rows.filter((r) => (v.key === "cut" ? isCut(r) : v.key === "revised" ? r.kind === "revision" : true)).length}
                  </span>
                ) : null}
              </button>
            </HoverHint>
          ))}
        </div>
        <label className="ml-auto flex items-center gap-2 text-sm text-zinc-600">
          Year
          <select className="rounded-md border border-zinc-300 px-2 py-1 text-sm" value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="all">All years</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>
      {query.isPending ? <LoadingState label="Reading the true-up orders..." /> : null}
      {query.isError ? <ErrorState error={query.error} label="Could not load the true-up ledger" /> : null}
      {query.isSuccess && !shown.length ? <EmptyState>Nothing here.</EmptyState> : null}
      {shown.length ? (
        <Card>
          <CardBody>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
                    <th className="px-2 py-2 font-medium">Year</th>
                    <th className="px-2 py-2 font-medium">Item</th>
                    <th className="px-2 py-2 font-medium">{view === "revised" ? "" : "Claimed"}</th>
                    <th className="px-2 py-2 font-medium">{view === "revised" ? "Now allowed" : "Allowed"}</th>
                    <th className="px-2 py-2 font-medium">Short by</th>
                    <th className="px-2 py-2 font-medium">Rates</th>
                    <th className="px-2 py-2 font-medium">Why it was cut</th>
                    <th className="px-2 py-2 font-medium">What happened next</th>
                    <th className="px-2 py-2 font-medium">Order</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((line) => (
                    <LineRow key={line.id} line={line} onOpen={setOpened} />
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      ) : null}
      {opened?.open_decision_id ? (
        <SourceDocumentViewer
          source={{ type: "decision", id: opened.open_decision_id, label: opened.page ? `${opened.order_label} · p${opened.page}` : opened.order_label }}
          onClose={() => setOpened(null)}
          startPage={opened.page}
        />
      ) : null}
    </div>
  );
}
